/**
 * Analytics helper — thin wrapper over PostHog.
 *
 * PostHog is initialised via the inline snippet in /app/frontend/public/index.html,
 * which means `window.posthog` is available as soon as the page loads (autocapture,
 * session recording, pageviews, and UTM enrichment are all on by default).
 *
 * This helper provides:
 *   - `track(event, props)`      — capture a custom conversion event
 *   - `identify(distinctId, props)` — tie later events to a logged-in user
 *   - `reset()`                   — call on logout
 *   - `<PageviewTracker />`       — fires $pageview on every SPA route change
 *
 * Every call is a safe no-op if posthog is blocked (adblockers, DoNotTrack etc.)
 * so feature code never has to guard the call itself.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function ph() {
  // posthog is attached to window by the snippet in index.html
  return (typeof window !== 'undefined' && window.posthog) || null;
}

export function track(event, props = {}) {
  try {
    ph()?.capture(event, props);
  } catch (err) {
    // Never let analytics break product flow — log and move on.
    // eslint-disable-next-line no-console
    console.warn('[analytics] capture failed:', err);
  }
}

export function identify(distinctId, props = {}) {
  try {
    if (!distinctId) return;
    ph()?.identify(distinctId, props);
  } catch {
    /* noop */
  }
}

export function setPersonProperties(props) {
  try {
    ph()?.setPersonProperties(props);
  } catch {
    /* noop */
  }
}

export function reset() {
  try {
    ph()?.reset();
  } catch {
    /* noop */
  }
}

/**
 * Mount this once under <Router> to auto-track SPA route changes as $pageview
 * (the inline snippet only fires on initial load; React Router handles the rest).
 */
export function PageviewTracker() {
  const location = useLocation();
  useEffect(() => {
    const posthog = ph();
    if (!posthog) return;
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path: location.pathname,
      search: location.search,
    });
  }, [location.pathname, location.search]);
  return null;
}
